// Auto-detect API URL based on current hostname
const getAPIBaseURL = () => {
  // Use environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, use same origin (API is on same server)
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${hostname}${port}/api`;
  }
  
  // Fallback for SSR or testing
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getAPIBaseURL();

// Helper function to get auth token
const getToken = () => {
  return localStorage.getItem('nitroToken') || localStorage.getItem('token');
};

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(`Unexpected response type: ${contentType}`);
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Auth API
export const authAPI = {
  register: async (discordUsername, email, password) => {
    const data = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ discordUsername, email, password }),
    });
    if (data.token) {
      localStorage.setItem('nitroToken', data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('nitroCurrentUser', JSON.stringify({ id: data.user.id }));
    }
    return data;
  },

  login: async (email, password, discordUsername) => {
    const data = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, discordUsername }),
    });
    if (data.token) {
      localStorage.setItem('nitroToken', data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('nitroCurrentUser', JSON.stringify({ id: data.user.id }));
    }
    return data;
  },

  getCurrentUser: async () => {
    return await apiRequest('/auth/me');
  },

  logout: () => {
    localStorage.removeItem('nitroToken');
    localStorage.removeItem('token');
    localStorage.removeItem('nitroCurrentUser');
  },
};

// Users API
export const usersAPI = {
  getAll: async () => {
    return await apiRequest('/users');
  },

  getById: async (id) => {
    return await apiRequest(`/users/${id}`);
  },

  search: async (query) => {
    return await apiRequest(`/users/search/${encodeURIComponent(query)}`);
  },

  updateEntries: async (userId, giveawayId, count, days) => {
    return await apiRequest(`/users/${userId}/entries`, {
      method: 'PUT',
      body: JSON.stringify({ giveawayId, count, days }),
    });
  },

  updateRole: async (userId, role) => {
    return await apiRequest(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  lockUser: async (userId, locked) => {
    return await apiRequest(`/users/${userId}/lock`, {
      method: 'PUT',
      body: JSON.stringify({ locked }),
    });
  },

  addBoost: async (userId, type, days) => {
    return await apiRequest(`/users/${userId}/boosts`, {
      method: 'POST',
      body: JSON.stringify({ type, days }),
    });
  },
};

// Giveaways API
export const giveawaysAPI = {
  getAll: async () => {
    return await apiRequest('/giveaways');
  },

  getActive: async () => {
    return await apiRequest('/giveaways/active');
  },

  getById: async (id) => {
    return await apiRequest(`/giveaways/${id}`);
  },

  create: async (giveawayData) => {
    return await apiRequest('/giveaways', {
      method: 'POST',
      body: JSON.stringify(giveawayData),
    });
  },

  update: async (id, giveawayData) => {
    return await apiRequest(`/giveaways/${id}`, {
      method: 'PUT',
      body: JSON.stringify(giveawayData),
    });
  },

  delete: async (id) => {
    return await apiRequest(`/giveaways/${id}`, {
      method: 'DELETE',
    });
  },

  enter: async (id) => {
    return await apiRequest(`/giveaways/${id}/enter`, {
      method: 'POST',
    });
  },
};

// Announcements API
export const announcementsAPI = {
  getAll: async () => {
    return await apiRequest('/announcements');
  },

  getById: async (id) => {
    return await apiRequest(`/announcements/${id}`);
  },

  create: async (announcementData) => {
    return await apiRequest('/announcements', {
      method: 'POST',
      body: JSON.stringify(announcementData),
    });
  },

  update: async (id, announcementData) => {
    return await apiRequest(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(announcementData),
    });
  },

  delete: async (id) => {
    return await apiRequest(`/announcements/${id}`, {
      method: 'DELETE',
    });
  },
};

// Messages API
export const messagesAPI = {
  getAll: async () => {
    return await apiRequest('/messages');
  },

  getConversation: async (userId) => {
    return await apiRequest(`/messages/conversation/${userId}`);
  },

  send: async (to, content) => {
    return await apiRequest('/messages', {
      method: 'POST',
      body: JSON.stringify({ to, content }),
    });
  },

  markAsRead: async (id) => {
    return await apiRequest(`/messages/${id}/read`, {
      method: 'PUT',
    });
  },

  markAllAsRead: async () => {
    return await apiRequest('/messages/read-all', {
      method: 'PUT',
    });
  },
};

// Chat API
export const chatAPI = {
  getMessages: async (limit = 200) => {
    return await apiRequest(`/chat?limit=${limit}`);
  },

  sendMessage: async (message, image) => {
    return await apiRequest('/chat', {
      method: 'POST',
      body: JSON.stringify({ message, image }),
    });
  },

  deleteMessage: async (id) => {
    return await apiRequest(`/chat/${id}`, {
      method: 'DELETE',
    });
  },
};

// Wiki API
export const wikiAPI = {
  getAll: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.category) params.append('category', options.category);
    if (options.limit) params.append('limit', options.limit);
    return await apiRequest(`/wiki/pages?${params.toString()}`);
  },

  getBySlug: async (slug) => {
    return await apiRequest(`/wiki/pages/${slug}`);
  },

  create: async (pageData) => {
    return await apiRequest('/wiki/pages', {
      method: 'POST',
      body: JSON.stringify(pageData),
    });
  },

  update: async (id, pageData) => {
    return await apiRequest(`/wiki/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pageData),
    });
  },

  delete: async (id) => {
    return await apiRequest(`/wiki/pages/${id}`, {
      method: 'DELETE',
    });
  },

  approve: async (id) => {
    return await apiRequest(`/wiki/pages/${id}/approve`, {
      method: 'POST',
    });
  },

  getVersions: async (id) => {
    return await apiRequest(`/wiki/pages/${id}/versions`);
  },

  rollback: async (id, version) => {
    return await apiRequest(`/wiki/pages/${id}/rollback/${version}`, {
      method: 'POST',
    });
  },
};

// DropCord API
export const dropcordAPI = {
  getServers: async () => {
    return await apiRequest('/dropcord/servers');
  },

  getServer: async (id) => {
    return await apiRequest(`/dropcord/servers/${id}`);
  },

  createServer: async (serverData) => {
    return await apiRequest('/dropcord/servers', {
      method: 'POST',
      body: JSON.stringify(serverData),
    });
  },

  createChannel: async (serverId, channelData) => {
    return await apiRequest(`/dropcord/servers/${serverId}/channels`, {
      method: 'POST',
      body: JSON.stringify(channelData),
    });
  },

  getMessages: async (channelId, options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.before) params.append('before', options.before);
    return await apiRequest(`/dropcord/channels/${channelId}/messages?${params.toString()}`);
  },

  sendMessage: async (channelId, messageData) => {
    return await apiRequest(`/dropcord/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  },

  joinServer: async (serverId) => {
    return await apiRequest(`/dropcord/servers/${serverId}/join`, {
      method: 'POST',
    });
  },

  leaveServer: async (serverId) => {
    return await apiRequest(`/dropcord/servers/${serverId}/leave`, {
      method: 'POST',
    });
  },

  getDMs: async (limit = 50) => {
    return await apiRequest(`/dropcord/dms?limit=${limit}`);
  },

  sendDM: async (recipientId, content, attachmentUrl) => {
    return await apiRequest('/dropcord/dms', {
      method: 'POST',
      body: JSON.stringify({ recipientId, content, attachmentUrl }),
    });
  },
};

// System API
export const systemAPI = {
  getApps: async () => {
    return await apiRequest('/system/apps');
  },

  getStats: async () => {
    return await apiRequest('/system/stats');
  },
};

export default { authAPI, usersAPI, giveawaysAPI, announcementsAPI, messagesAPI, chatAPI, wikiAPI, dropcordAPI, systemAPI };
